import { useEffect, useRef, useState } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { Plus, Search, Upload } from 'lucide-react'
import { toast } from 'sonner'

import type { ImportResult } from '@/lib/api/customer-api'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog'
import { CustomerForm } from '@/components/forms/customer-form'
import { ImportResultDialog } from '@/components/import-result-dialog'
import {
    useCustomerGroups,
    useCustomers,
    useImportCustomers,
} from '@/hooks/use-customer-query'
import { formatVnd } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute('/_app/_authed/_sidebar/customers/')({
    component: CustomersPage,
})

function CustomersPage() {
    const [search, setSearch] = useState('')
    const [groupId, setGroupId] = useState('')
    const [page, setPage] = useState(1)
    const [dialogOpen, setDialogOpen] = useState(false)
    const [importResult, setImportResult] = useState<ImportResult | null>(null)
    const [importResultOpen, setImportResultOpen] = useState(false)
    const fileInputRef = useRef<HTMLInputElement>(null)
    const importMutation = useImportCustomers()

    // Listen for SSE import result events
    useEffect(() => {
        const handler = (e: Event) => {
            const result = (e as CustomEvent<ImportResult>).detail
            setImportResult(result)
            setImportResultOpen(true)
        }
        window.addEventListener('customer-import-result', handler)
        return () =>
            window.removeEventListener('customer-import-result', handler)
    }, [])

    const { data: groups = [] } = useCustomerGroups()
    const { data, isLoading } = useCustomers({
        search: search || undefined,
        customer_group_id: groupId || undefined,
        page,
        per_page: 20,
    })

    const customers = data?.data ?? []
    const meta = data?.meta

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h1 className="text-xl font-semibold">{m.customer_title()}</h1>
                <div className="flex gap-2 items-center">
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".xlsx"
                        className="hidden"
                        onChange={(e) => {
                            const file = e.target.files?.[0]
                            if (!file) return
                            importMutation.mutate(file, {
                                onSuccess: () => {
                                    toast.info(m.import_processing())
                                },
                                onError: () =>
                                    toast.error(m.import_upload_failed()),
                            })
                            e.target.value = ''
                        }}
                    />
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={importMutation.isPending}
                    >
                        <Upload className="size-4" />
                        {importMutation.isPending
                            ? m.import_importing()
                            : m.import_excel()}
                    </Button>
                    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                        <DialogTrigger asChild>
                            <Button size="sm">
                                <Plus className="size-4" />
                                {m.customer_add_new()}
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-2xl">
                            <DialogHeader>
                                <DialogTitle>
                                    {m.customer_add_new()}
                                </DialogTitle>
                            </DialogHeader>
                            <CustomerForm
                                onSuccess={() => setDialogOpen(false)}
                                onCancel={() => setDialogOpen(false)}
                            />
                        </DialogContent>
                    </Dialog>
                </div>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-3">
                <div className="relative flex-1 min-w-[200px]">
                    <Search className="absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
                    <Input
                        placeholder={m.customer_search()}
                        className="pl-9"
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value)
                            setPage(1)
                        }}
                    />
                </div>
                <Select
                    value={groupId || 'all'}
                    onValueChange={(v) => {
                        setGroupId(v === 'all' ? '' : v)
                        setPage(1)
                    }}
                >
                    <SelectTrigger className="w-48">
                        <SelectValue placeholder={m.customer_all_groups()} />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">
                            {m.customer_all_groups()}
                        </SelectItem>
                        {groups.map((g) => (
                            <SelectItem key={g.id} value={g.id}>
                                {g.name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {/* Table */}
            <div className="rounded-md border">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{m.customer_code()}</TableHead>
                            <TableHead>{m.customer_name_label()}</TableHead>
                            <TableHead>{m.customer_phone_label()}</TableHead>
                            <TableHead>{m.customer_group()}</TableHead>
                            <TableHead>{m.customer_credit_limit()}</TableHead>
                            <TableHead>{m.customer_outstanding_debt()}</TableHead>
                            <TableHead>{m.product_status()}</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell
                                    colSpan={7}
                                    className="text-center text-muted-foreground py-8"
                                >
                                    {m.common_loading()}
                                </TableCell>
                            </TableRow>
                        ) : customers.length === 0 ? (
                            <TableRow>
                                <TableCell
                                    colSpan={7}
                                    className="text-center text-muted-foreground py-8"
                                >
                                    {m.customer_no_data()}
                                </TableCell>
                            </TableRow>
                        ) : (
                            customers.map((c) => (
                                <TableRow key={c.id}>
                                    <TableCell className="font-mono text-xs">
                                        {c.code}
                                    </TableCell>
                                    <TableCell>
                                        <Link
                                            to="/customers/$customerId"
                                            params={{ customerId: c.id }}
                                            className="font-medium text-primary underline underline-offset-2 decoration-primary/40 hover:decoration-primary"
                                        >
                                            {c.name}
                                        </Link>
                                    </TableCell>
                                    <TableCell>{c.phone ?? '—'}</TableCell>
                                    <TableCell>
                                        {c.customer_group?.name ?? '—'}
                                    </TableCell>
                                    <TableCell className="tabular-nums">
                                        {formatVnd(c.credit_limit)}
                                    </TableCell>
                                    <TableCell className="tabular-nums">
                                        {formatVnd(c.current_debt)}
                                    </TableCell>
                                    <TableCell>
                                        <Badge
                                            variant={
                                                c.is_active
                                                    ? 'success'
                                                    : 'secondary'
                                            }
                                        >
                                            {c.is_active
                                                ? m.customer_active()
                                                : m.customer_inactive()}
                                        </Badge>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            {/* Pagination */}
            {meta && meta.total_pages > 1 && (
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>{m.customer_total_count({ count: meta.total })}</span>
                    <div className="flex gap-2">
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={page === 1}
                            onClick={() => setPage((p) => p - 1)}
                        >
                            {m.common_prev()}
                        </Button>
                        <span className="flex items-center px-2">
                            {page} / {meta.total_pages}
                        </span>
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={page >= meta.total_pages}
                            onClick={() => setPage((p) => p + 1)}
                        >
                            {m.common_next()}
                        </Button>
                    </div>
                </div>
            )}

            {/* Import result dialog */}
            <ImportResultDialog
                result={importResult}
                open={importResultOpen}
                onOpenChange={setImportResultOpen}
            />
        </div>
    )
}
